import { ChangeDetectionStrategy, Component, effect, OnDestroy, viewChild, ViewEncapsulation } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import { Employee } from '../../../generated/generated-types';
import { EmployeeEditWidgetComponent } from '../../hrm/components/employee-edit-widget/employee-edit-widget.component';

/** Host detail components that save the embedded employee themselves implement this to reset it afterwards. */
export interface EmbeddedEmployeeEditorHost {
  registerEmbeddedEmployeeEditor(editor: FormlyEmployeeEditComponent | null): void;
}

@Component({
  selector: 'app-formly-employee-edit',
  templateUrl: './formly-employee-edit.component.html',
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    EmployeeEditWidgetComponent
  ]
})
export class FormlyEmployeeEditComponent extends FieldType<FieldTypeConfig> implements OnDestroy {
  private readonly employeeWidget = viewChild(EmployeeEditWidgetComponent);
  private employeeWasDirty = false;

  constructor() {
    super();
    effect(() => {
      if (this.employeeWidget()) {
        this.host()?.registerEmbeddedEmployeeEditor(this);
      }
    });
    effect(() => {
      const widget = this.employeeWidget();
      const employeeDirty = !!widget?.modelIsDirtySig();
      const employeeControl = this.form?.get('employee');
      if (!employeeControl) {
        return;
      }

      if (employeeDirty) {
        employeeControl.markAsDirty();
        this.options?.formState?.detailComponent?.modelIsDirtySig.set(true);
        this.employeeWasDirty = true;
        return;
      }

      if (this.employeeWasDirty) {
        employeeControl.markAsPristine();
        this.options?.formState?.detailComponent?.notifyDirtyRecheck();
        this.employeeWasDirty = false;
      }
    });
  }

  ngOnDestroy(): void {
    this.host()?.registerEmbeddedEmployeeEditor(null);
  }

  /**
   * Make the saved employee the widget's new baseline, so it stops reporting dirty.
   * The dirty effect above then marks the `employee` control pristine and rechecks the host.
   */
  async applySavedEmployee(employee: Employee): Promise<void> {
    await this.employeeWidget()?.setModel(employee);
  }

  private host(): EmbeddedEmployeeEditorHost | null {
    const host = this.options?.formState?.detailComponent as Partial<EmbeddedEmployeeEditorHost> | undefined;
    return typeof host?.registerEmbeddedEmployeeEditor === 'function' ? host as EmbeddedEmployeeEditorHost : null;
  }

  employeeModelId(): string | null {
    const employeeId = (this.props as { employeeId?: string }).employeeId;
    return employeeId || null;
  }

  onEmployeeChange(value: FormGroup) {
    this.form.get('employee')?.patchValue(value, { emitEvent: false });
  }
}
